import { useState } from "react";
import Icon from "./Icon";
import { Btn, PillTag } from "./UI";
import {
  useStoreQuery,
  useStoreMutation,
  queries,
  mutations,
} from "@/lib/store";

/**
 * VerseCheckInSheet \u2014 bottom-sheet modal where a sister marks "I sat with
 * this verse" for the current week. Reflection is optional. She may share
 * to the community feed, or keep it between her and the Lord.
 */
interface Props {
  userId: string;
  onClose: () => void;
}

export default function VerseCheckInSheet({ userId, onClose }: Props) {
  const verse = useStoreQuery(() => queries.currentVerseOfWeek());
  const existing = useStoreQuery(() => queries.verseCheckInForUser(userId));
  const mark = useStoreMutation(mutations.markVerseCheckIn);
  const unmark = useStoreMutation(mutations.unmarkVerseCheckIn);

  const [reflection, setReflection] = useState<string>(existing?.reflection ?? "");
  const [share, setShare] = useState<boolean>(existing?.sharedToFeed ?? true);

  if (!verse) return null;

  const onSave = () => {
    mark({ userId, reflection, sharedToFeed: share });
    onClose();
  };

  const onClear = () => {
    unmark(userId);
    onClose();
  };

  return (
    <div
      className="fixed inset-0 flex items-end justify-center"
      style={{ background: "rgba(22,56,40,0.55)", zIndex: 60 }}
      onClick={onClose}
      data-testid="verse-checkin-overlay"
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          width: "100%",
          maxWidth: 420,
          background: "var(--mnf-cream)",
          borderRadius: "24px 24px 0 0",
          padding: 22,
          maxHeight: "86vh",
          overflowY: "auto",
        }}
      >
        <div className="flex items-start justify-between mb-3 gap-3">
          <div>
            <PillTag tone="gold">Verse of the week</PillTag>
            <h2
              className="mnf-serif"
              style={{
                fontSize: 22,
                fontWeight: 500,
                color: "var(--mnf-fd)",
                lineHeight: 1.18,
                marginTop: 8,
              }}
            >
              Sit with this verse
            </h2>
          </div>
          <button
            onClick={onClose}
            data-testid="button-close-verse-checkin"
            className="hover-elevate"
            style={{
              width: 36,
              height: 36,
              borderRadius: 999,
              background: "rgba(30,74,53,0.06)",
              color: "var(--mnf-f)",
              border: "none",
              cursor: "pointer",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
            aria-label="Close"
          >
            <Icon name="close" size={18} />
          </button>
        </div>

        <blockquote
          className="mnf-serif italic"
          style={{
            fontSize: 16,
            lineHeight: 1.45,
            color: "var(--mnf-fd)",
            margin: 0,
            paddingLeft: 14,
            borderLeft: "2px solid rgba(160,120,32,0.5)",
            marginBottom: 8,
          }}
        >
          "{verse.text}"
        </blockquote>
        <p
          className="mnf-eyebrow"
          style={{ color: "var(--mnf-t)", fontSize: 10, marginBottom: 16 }}
        >
          {verse.reference}
        </p>

        <p
          className="mnf-body"
          style={{
            fontSize: 12.5,
            color: "var(--mnf-tg)",
            lineHeight: 1.55,
            marginBottom: 14,
          }}
        >
          {verse.invitation}
        </p>

        <label className="block mb-3">
          <span
            className="mnf-eyebrow block"
            style={{ color: "var(--mnf-t)", marginBottom: 6 }}
          >
            Your reflection (optional)
          </span>
          <textarea
            value={reflection}
            onChange={(e) => setReflection(e.target.value)}
            data-testid="textarea-verse-reflection"
            rows={4}
            placeholder="What did the Lord whisper through this verse this week, sister?"
            className="mnf-body w-full"
            style={{
              padding: 12,
              borderRadius: 14,
              border: "1px solid rgba(30,74,53,0.16)",
              background: "#fff",
              color: "var(--mnf-fd)",
              fontSize: 13.5,
              lineHeight: 1.5,
              resize: "none",
              outline: "none",
              fontFamily: "inherit",
            }}
          />
        </label>

        <button
          onClick={() => setShare(!share)}
          data-testid="toggle-verse-share"
          aria-pressed={share}
          className="w-full flex items-center justify-between gap-3 hover-elevate"
          style={{
            padding: "12px 14px",
            borderRadius: 14,
            border: "1px solid rgba(30,74,53,0.10)",
            background: "#fff",
            textAlign: "left",
            cursor: "pointer",
            fontFamily: "inherit",
            marginBottom: 14,
          }}
        >
          <span className="flex-1 min-w-0">
            <span
              className="mnf-serif block"
              style={{ fontSize: 14, fontWeight: 500, color: "var(--mnf-fd)" }}
            >
              Share with my sisters
            </span>
            <span
              className="mnf-body block"
              style={{
                fontSize: 11.5,
                color: "var(--mnf-tg)",
                lineHeight: 1.4,
                marginTop: 2,
              }}
            >
              {share
                ? "Your reflection will appear in the community feed."
                : "Kept between you and the Lord."}
            </span>
          </span>
          <SwitchPip active={share} />
        </button>

        <div className="flex gap-2">
          {existing ? (
            <Btn variant="ghost" size="md" onClick={onClear} data-testid="button-clear-verse-checkin">
              Clear
            </Btn>
          ) : (
            <Btn variant="ghost" size="md" onClick={onClose}>
              Not yet
            </Btn>
          )}
          <Btn
            variant="primary"
            size="md"
            onClick={onSave}
            className="flex-1"
            data-testid="button-save-verse-checkin"
          >
            {existing ? "Update reflection" : "Mark as reflected"}
          </Btn>
        </div>

        <p
          className="mnf-body italic"
          style={{
            fontSize: 11.5,
            color: "var(--mnf-tg)",
            lineHeight: 1.55,
            marginTop: 14,
            textAlign: "center",
          }}
        >
          One verse, one week. No streaks, no scores.
        </p>
      </div>
    </div>
  );
}

function SwitchPip({ active }: { active: boolean }) {
  return (
    <span
      style={{
        width: 38,
        height: 22,
        borderRadius: 999,
        background: active ? "var(--mnf-f)" : "rgba(30,74,53,0.18)",
        position: "relative",
        transition: "background 180ms ease",
        flexShrink: 0,
        display: "inline-block",
      }}
    >
      <span
        style={{
          position: "absolute",
          top: 2,
          left: active ? 18 : 2,
          width: 18,
          height: 18,
          borderRadius: 999,
          background: "#F5F0E8",
          transition: "left 180ms ease",
          boxShadow: "0 1px 3px rgba(0,0,0,0.2)",
        }}
      />
    </span>
  );
}
